/* eslint-disable react-hooks/exhaustive-deps */
import { useTranslate } from 'hooks/useTranslate';
import { useEffect, useState } from 'react';

import { useScroll } from 'modules/_shared/contexts/ScrollContext';
import PartnerDividends from 'modules/partners/components/PartnerDividends';
import DataTablePagination from 'modules/_shared/components/Tables/DataTablePagination';
import { useGetSocietySharesTitleDocument } from 'modules/partners/pages/TitleRegistryBook/hooks/useGetSocietySharesTitleDocument';

import { getTransactionKey } from 'modules/partners/utils';
import {
  hasPaginationChanged,
  mapPaginationToPaginationData,
} from 'modules/_shared/components/Pagination/paginationUtils';
import TableSkeleton from 'modules/_shared/components/Tables/TableSkeleton';
import PartnerTransactionsTable from './PartnerTransactionsTable';
import { usePartnerTransactions } from './hooks/usePartnerTransactions';
import PartnerTransactionsRow from './PartnerTransactionsRow';
import { getInvertedIndex } from './utils/getInvertedIndex';
import { getDocumentsFromTransaction } from './utils/getDocumentsFromTransaction';
import { getTagFromTransaction } from './utils/getTagFromTransaction';
import { PartnerDividendsProvider } from '../PartnerDividends/context/PartnerDividendsContext';

const INITIAL_PAGE = 1;
const INITIAL_ROWS_PER_PAGE = 20;

const PartnerTransactions = ({ shares, society, partner }) => {
  const { t } = useTranslate();
  const { actions } = useScroll();

  const [rows, setRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(INITIAL_PAGE);
  const [rowsPerPage, setRowsPerPage] = useState(INITIAL_ROWS_PER_PAGE);
  const [paginationData, setPaginationData] = useState({
    customItemPerPage: INITIAL_ROWS_PER_PAGE,
    itemPerPage: INITIAL_ROWS_PER_PAGE,
    currentPage: INITIAL_PAGE,
    totalItems: 0,
    totalPages: 0,
  });

  const usePartnerTransactionsResult = usePartnerTransactions({
    partner,
    society,
    page: currentPage,
    items: rowsPerPage,
  });

  const { partnerTransactions, partnerShareTitles, documents } =
    usePartnerTransactionsResult.data;

  const {
    isLoading: isLoadingTransactions,
    isLoadingSharesTitles,
    pagination,
  } = usePartnerTransactionsResult;

  actions.handleScroll();

  const { document: societySharesTitleDocument } =
    useGetSocietySharesTitleDocument({ society }).data;

  const handleChangePage = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleChangeRowsPerPage = (newRowsCount) => {
    setRowsPerPage(newRowsCount);
  };

  useEffect(() => {
    if (!partnerTransactions) return;

    setRows(
      partnerTransactions?.map((transaction, index) => (
        <PartnerTransactionsRow
          key={getTransactionKey(transaction)}
          index={getInvertedIndex(index, partnerTransactions?.length)}
          sharesTitles={partnerShareTitles}
          isLoadingSharesTitles={isLoadingSharesTitles}
          society={society}
          transaction={transaction}
          societySharesTitleDocument={societySharesTitleDocument}
          documents={getDocumentsFromTransaction(transaction, documents)}
          tag={getTagFromTransaction(transaction, shares, society)}
        />
      ))
    );
  }, [
    partnerTransactions,
    documents,
    partnerShareTitles,
    isLoadingSharesTitles,
  ]);

  useEffect(() => {
    if (pagination && hasPaginationChanged(pagination, paginationData)) {
      setPaginationData((prevState) => ({
        ...prevState,
        ...mapPaginationToPaginationData(pagination),
      }));
    }
  }, [pagination]);

  return (
    <>
      <div className="nk-block-head">
        <div className="nk-block-head-content">
          <h4 className="nk-block-title">{t('PartnerTransactions')}</h4>
          <div className="nk-block-des">
            <p>
              {t('TransactionsInSocietyName', {
                societyName: society.name,
                partnerName: partner.name || '',
              })}
            </p>
          </div>
        </div>
      </div>

      {isLoadingTransactions ? (
        <div className="card card-bordered">
          <TableSkeleton rows={3} />
        </div>
      ) : (
        <>
          <div className="nk-block nk-block-lg">
            <PartnerTransactionsTable
              rows={rows}
              hasShareTags={society?.hasShareTags}
            />
          </div>

          <DataTablePagination
            itemPerPage={paginationData.itemPerPage}
            totalItems={paginationData.totalItems}
            currentPage={paginationData.currentPage}
            customItemPerPage={paginationData.customItemPerPage}
            paginate={handleChangePage}
            setRowsPerPage={setRowsPerPage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
            className="card-pagination-small border border-top-0 bg-lighter"
          />
        </>
      )}

      <div className="nk-block-head mt-5" id="dividends-table">
        <div className="nk-block-head-content">
          <h4 className="nk-block-title">{t('Dividends')}</h4>
          <div className="nk-block-des">
            <p>
              {t('DividendsDetails', {
                societyName: society.name,
              })}
            </p>
          </div>
        </div>
      </div>

      <div className="nk-block nk-block-lg">
        <PartnerDividendsProvider partner={partner} showTitle={false}>
          <PartnerDividends />
        </PartnerDividendsProvider>
      </div>
    </>
  );
};

export default PartnerTransactions;
